import { IS_ETS_NEXT } from '@/utils/constants';
import { hasReadPermission } from '@/utils/hubPermissions/hasPermissions';

const exposedRoutes = [
  '/quicksight',
  '/hub',
  '/personal-settings/change-password',
  '/personal-settings/hub-releases',
  '/personal-settings/hub-releases/[id]',
  '/personal-settings/terms-of-use',
  '/personal-settings/privacy-policy',
  '/personal-settings/profile',
];

export const routeGuardsConfig = {
  'qc-mail-ticketing': {
    requiredPermission: ['qcMailTicketing'],
    schedules: {
      requiredPermission: ['qcMailTicketingSchedules'],
    },
    tables: {
      requiredPermission: ['qcMailTicketingTables'],
    },
    resrules: {
      requiredPermission: ['qcMailTicketingResrules'],
    },
    'reporting-and-analytics': {
      etsNextLockedDown: true,
      requiredPermission: ['qcMailTicketingReportingAndAnalytics'],
    },
    'data-insights': {
      etsNextLockedDown: true,
      requiredPermission: ['qcMailTicketingDataInsights'],
    },
    'demo-reporting-and-analytics': {
      etsNextLockedDown: true,
      requiredPermission: ['qcMailTicketingDemoReportingAndAnalytics'],
    },
  },

  'queue-monitor': {
    requiredPermission: ['queueMonitor'],
  },

  'travel-authorization': {
    etsNextLockedDown: true,
    requiredPermission: ['travelAuthorization'],
    'pending-requests': {
      requiredPermission: ['travelAuthorizationPendingRequests'],
    },
    'search-requests': {
      requiredPermission: ['travelAuthorizationSearchRequests'],
    },
    'travel-policy': {
      requiredPermission: ['travelAuthorizationTravelPolicy'],
    },
    meetings: {
      requiredPermission: ['travelAuthorizationMeetings'],
    },
    'business-insights': {
      requiredPermission: ['travelAuthorizationBusinessInsights'],
    },
    'transaction-logs': {
      requiredPermission: ['travelAuthorizationTransactionLogs'],
    },
    'document-logs': {
      requiredPermission: ['travelAuthorizationDocumentLogs'],
    },
  },

  tickettrak: {
    requiredPermission: ['tickettrak'],
  },

  'table-connector': {
    etsNextLockedDown: true,
    requiredPermission: ['tableConnector'],
  },

  'event-driven-actions': {
    requiredPermission: ['eda'],
  },

  'ibank-reporting': {
    etsNextLockedDown: true,
    requiredPermission: ['ibankReporting'],
    login: {
      requiredPermission: ['ibankReportingLogin'],
    },
    'event-logs': {
      requiredPermission: ['ibankReportingEventLogs'],
    },
  },

  administration: {
    requiredPermission: ['administration'],
    'user-management': {
      requiredPermission: ['administrationUserManagement'],
    },
    'site-configuration': {
      requiredPermission: ['administrationSiteConfiguration'],
    },
    'asset-management': {
      requiredPermission: ['administrationAssetManagement'],
    },
    'activity-log': {
      requiredPermission: ['administrationActivityLogs'],
    },
    'role-management': {
      requiredPermission: ['administrationRoleManagement'],
    },
  },

  'cis-internal-tools': {
    requiredPermission: ['cisInternal'],
    internalOnly: true,
    organizations: {
      requiredPermission: ['cisInternalOrganizations'],
    },
    libraries: {
      requiredPermission: ['cisInternalLibraries'],
    },
    'schedule-mover': {
      requiredPermission: ['cisInternalScheduleMover'],
    },
    'engine-monitor': {
      requiredPermission: ['cisInternalEngineMonitor'],
    },
    'queue-monitor': {
      requiredPermission: ['cisInternalQueueMonitor'],
    },
    'iqcx-alert-notifications': {
      requiredPermission: ['cisInternalIqcxAlertNotifications'],
    },
    'email-notification': {
      requiredPermission: ['cisInternalEmailNotifications'],
    },
    'dynamic-reporting': {
      requiredPermission: ['cisInternalDynamicReporting'],
    },
    'gtr-stats': {
      requiredPermission: ['cisInternalGtrStats'],
    },
    'feature-flag-management': {
      requiredPermission: ['cisInternalFeatureFlags'],
    },
  },
  'personal-settings': {
    'hub-releases': {
      etsNextLockedDown: true,
      '[id]': {
        etsNextLockedDown: true,
      },
    },
  },
  quicksight: {
    etsNextOnly: true,
  },
};

export function aggregateRouteGuards(path) {
  let requiredPermissions = [];
  let internalOnly = false;
  let etsNextLockedDown = false;
  let etsNextOnly = false;

  let pointer = routeGuardsConfig;
  for (let routeSegment of path) {
    if (!pointer[routeSegment]) break;
    pointer = pointer[routeSegment];
    if (pointer.requiredPermission)
      requiredPermissions = requiredPermissions.concat(
        pointer.requiredPermission
      );
    if (pointer.internalOnly) internalOnly = true;
    if (pointer.etsNextLockedDown) etsNextLockedDown = true;
    if (pointer.etsNextOnly) etsNextOnly = true;
  }

  return { requiredPermissions, internalOnly, etsNextLockedDown, etsNextOnly };
}

export function isPermissionedRoute(route, user, org) {
  const path = route.split('/').filter((segment) => segment);
  const { requiredPermissions, internalOnly, etsNextLockedDown, etsNextOnly } =
    aggregateRouteGuards(path);

  if (internalOnly && !user.isInternal) return false;

  if (etsNextLockedDown && IS_ETS_NEXT) return false;

  if (etsNextOnly && !IS_ETS_NEXT) return false;

  if (!requiredPermissions.length && !exposedRoutes.includes(route)) {
    console.error('Route is not configured!');
    return false;
  }

  for (let requiredPermission of requiredPermissions) {
    if (
      !hasReadPermission(user, requiredPermission) ||
      !hasReadPermission(org, requiredPermission)
    ) {
      return false;
    }
  }

  return true;
}
